/**
 * ONE resolver for everything the consumer funnel reads out of
 * `programs.public_config` (projected by `get_public_program_by_slug`).
 *
 * Why this file exists: S6/S7 had invented key names (`deliveryOptions`,
 * `orientationChoices`, `requirePanLast5`) that are NOT on the RPC allowlist, so
 * admin config was silently inert and the screens fell back to hardcoded values.
 * Every consumer read now goes through here, against the allowlisted names:
 *
 *   cardFeeCents · pricingTiers · currency · fundingAmountsCents ·
 *   fundingCheckoutEnabled · freeAttemptsPerUser · allowPhotoOnly ·
 *   deliveryMethods · colorChoices · orientations · panDigitsRequired ·
 *   brandName · cardNetwork · numVariants
 *
 * SCOPE RESOLUTION (`panDigitsRequired`): the registry historically listed this
 * as a TENANT key (`tenants.config`). The consumer surface never receives
 * `tenants.config` — it only ever sees the program projection. The authoritative
 * value for /create is therefore the PROGRAM value, and that is what is used
 * here. Absent/0 = no card number is collected (Cardmoji's own prepaid card has
 * no PAN at order time).
 *
 * MONEY BOUNDARY: `fundingAmountsCents` is the customer's INTENDED LOAD, a
 * captured preference handed to the issuing bank at fulfilment. It is deliberately
 * kept out of `resolvePricing()` so it can never reach a fee or a Stripe total.
 * Cardmoji is not a money transmitter — the bank loads the card.
 */

export type DeliveryMethod = "in_branch" | "mail";
export type Orientation = "landscape" | "portrait";

export interface PricingTier {
  label: string;
  quantity: number;
  priceCents: number;
}

export interface ConsumerPricing {
  currency: string;
  /** The single-card fee Cardmoji charges. NEVER includes any load amount. */
  cardFeeCents: number | null;
  tiers: PricingTier[];
}

/** Programme config may spell delivery either way; normalise to the app's vocabulary. */
function normaliseDelivery(raw: unknown): DeliveryMethod | null {
  const v = String(raw ?? "").toLowerCase();
  if (v === "mail" || v === "postal" || v === "post") return "mail";
  if (v === "in_branch" || v === "branch" || v === "inbranch") return "in_branch";
  return null;
}

/** `deliveryMethods` (allowlisted). Falls back to post-only for the D2C surface. */
export function resolveDeliveryMethods(cfg: Record<string, any>): DeliveryMethod[] {
  const raw = cfg?.deliveryMethods;
  if (Array.isArray(raw)) {
    const out: DeliveryMethod[] = [];
    for (const item of raw) {
      const m = normaliseDelivery(item);
      if (m && !out.includes(m)) out.push(m);
    }
    if (out.length) return out;
  }
  return ["mail"];
}

/** `orientations` (allowlisted). */
export function resolveOrientations(cfg: Record<string, any>): Orientation[] {
  const raw = cfg?.orientations;
  if (Array.isArray(raw)) {
    const out = raw
      .map((o) => String(o).toLowerCase())
      .filter((o): o is Orientation => o === "landscape" || o === "portrait");
    if (out.length) return Array.from(new Set(out));
  }
  return ["landscape", "portrait"];
}

/**
 * `panDigitsRequired` (allowlisted, PROGRAM scope — see file header).
 * Returns 0 when no card number should be collected.
 */
export function resolvePanDigits(cfg: Record<string, any>): number {
  const n = Number(cfg?.panDigitsRequired);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(Math.max(Math.trunc(n), 3), 8);
}

/** Exact-length validator built from the configured digit count. */
export function panMatchesConfig(value: string, digits: number): boolean {
  if (digits <= 0) return true;
  return new RegExp(`^\\d{${digits}}$`).test(value.trim());
}

/** `cardFeeCents` / `pricingTiers` / `currency`. Display-only until B5. */
export function resolvePricing(cfg: Record<string, any>, fallbackCurrency?: string | null): ConsumerPricing {
  const fee = Number(cfg?.cardFeeCents);
  const tiersRaw = Array.isArray(cfg?.pricingTiers) ? cfg.pricingTiers : [];
  const tiers: PricingTier[] = tiersRaw
    .map((tRaw: any) => ({
      label: String(tRaw?.label ?? ""),
      quantity: Number(tRaw?.quantity ?? 0),
      priceCents: Number(tRaw?.priceCents ?? NaN),
    }))
    .filter((tier) => tier.label && Number.isFinite(tier.priceCents) && tier.priceCents >= 0);
  return {
    currency: String(cfg?.currency ?? fallbackCurrency ?? "USD"),
    cardFeeCents: Number.isFinite(fee) && fee >= 0 ? fee : null,
    tiers,
  };
}

/**
 * `fundingAmountsCents` — the INTENDED LOAD presets. Not pricing.
 * Never call this from anything that computes a charge.
 */
export function resolveLoadAmounts(cfg: Record<string, any>): number[] {
  const raw = cfg?.fundingAmountsCents;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0)
    .map((v) => Math.trunc(v));
}

/**
 * `maxLoadCents` — the configured single-load ceiling (allowlisted on the public
 * projection). The load screen's DEFAULT and every customer-facing statement of
 * the limit resolve from here; a typed figure would silently disagree with the
 * configured value. `null` = not configured, so callers must not state a limit.
 */
export function resolveMaxLoadCents(cfg: Record<string, any>): number | null {
  const n = Number(cfg?.maxLoadCents);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

/** `fundingCheckoutEnabled` — MUST stay false: Cardmoji never charges a load. */
export function loadCheckoutEnabled(cfg: Record<string, any>): boolean {
  return cfg?.fundingCheckoutEnabled === true;
}

/** `freeAttemptsPerUser` — the visible generation allowance. */
export function resolveFreeAttempts(cfg: Record<string, any>): number {
  const n = Number(cfg?.freeAttemptsPerUser);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 3;
}

/**
 * The price for THIS order at the given quantity.
 *
 * Screens must show one number — what the customer is about to pay — not the
 * whole tier menu. Showing the single price alongside a multi-card tier while
 * ordering one card reads as the higher charge. Both figures come from config —
 * no price is typed here or in this comment. Resolution order:
 *   1. an exact `pricingTiers` entry for the quantity,
 *   2. otherwise `cardFeeCents * quantity`,
 *   3. otherwise null (fee not configured → "confirmed at checkout").
 */
export function resolveOrderPrice(
  pricing: ConsumerPricing,
  quantity = 1,
): { cents: number | null; tierLabel: string | null } {
  const qty = Math.max(1, Math.trunc(quantity));
  const exact = pricing.tiers.find((tier) => tier.quantity === qty);
  if (exact) return { cents: exact.priceCents, tierLabel: exact.label };
  if (pricing.cardFeeCents !== null) return { cents: pricing.cardFeeCents * qty, tierLabel: null };
  return { cents: null, tierLabel: null };
}

/**
 * THE quantities a customer may choose, derived from configured tiers ONLY.
 *
 * Why this exists: the checkout used to hard-code quantity 1 and each extra card
 * became its own full-price order, so three cards cost 3 x the single fee
 * instead of the configured three-card price. The chooser offers exactly the
 * tiers that are configured (today: 1 and 3) — never an interpolated quantity,
 * because a quantity with no tier falls back to multiplying the single fee,
 * which is the defect. A single configured tier means no choice is offered.
 */
export function resolveQuantityOptions(
  pricing: ConsumerPricing,
): { quantity: number; label: string; cents: number }[] {
  return pricing.tiers
    .filter((tier) => Number.isFinite(tier.quantity) && tier.quantity >= 1)
    .map((tier) => ({
      quantity: Math.trunc(tier.quantity),
      label: tier.label,
      cents: tier.priceCents,
    }))
    .sort((a, b) => a.quantity - b.quantity);
}

/** A quantity is only usable if a tier is configured for it. Otherwise 1. */
export function normaliseQuantity(pricing: ConsumerPricing, quantity: unknown): number {
  const qty = Math.trunc(Number(quantity));
  if (!Number.isFinite(qty) || qty < 1) return 1;
  return resolveQuantityOptions(pricing).some((o) => o.quantity === qty) ? qty : 1;
}


/** `brandName` (allowlisted) — the issuer brand printed on the card. */
export function resolveBrandName(cfg: Record<string, any>, fallback?: string | null): string | null {
  const v = String(cfg?.brandName ?? "").trim();
  if (v) return v;
  const f = String(fallback ?? "").trim();
  return f || null;
}

export function formatMoney(cents: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)} ${currency}`;
  }
}

